import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {map, Observable, startWith} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';

@UntilDestroy()
@Component({
  selector: 'app-autocomplete-field',
  templateUrl: './autocomplete-field.component.html',
  styleUrls: ['./autocomplete-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteFieldComponent),
      multi: true,
    },
  ],
})

/**
 * This component consist with custom dropdown with input title and autocomplete input.
 * When user write some text on the input and this text contained one of the possible words (optionArr) which transfer as input from
 * father component - under input field will be displayed a hint with these wards
 * If any items of optionArr array does not consist this text will display a message "not found"
 * User can select one of hint option, and it will appear on the input field
 * Also user can separate input wards by coma and autocomplete will work for text after coma
 * This Autocomplete not sensitive to register and beginning/end spaces on the word
 * This component works based on the ControlValueAccessor. All words written by user on input will be cleaned, compare with optionArr,
 * transformed into the array with strings and on every change will emit it to the father component
 */
export class AutocompleteFieldComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  /**
   * list of autocomplete option
   */
  @Input() filterData: IDropdownObject[] | null = [];
  /**
   * title of autocomplete field
   */
  @Input() title = '';

  @Input() countryFilter!: boolean;
  @Input() set dynamicFilter(value: IDropdownObject[] | null) {
    if (!value) return;
    this.optionArr = value.map((item: IDropdownObject) => item.name);
  }
  @ViewChild('div') div!: ElementRef;

  /**
   * Track clicks on the document if this click was outside option popup div element
   * showPopupOption variable assigned false value (hide popup)
   *
   * @param { MouseEvent } event
   * @return void
   */
  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    if (this.filterData !== null) {
      this.showPopupOption = !!this.div.nativeElement.contains(event.target);
    }
  }

  /**
   * define show or hide autocomplete field
   */
  showHideObj = false;
  showPopupOption = false;
  /**
   * list of autocomplete option after filtering
   */
  filteredOptions!: Observable<string[]>;

  fieldControl = new UntypedFormControl();
  optionArr: string[] = [];
  notFound = false;
  ultimateOutputValue: IDropdownObject[] = [];

  onChange = (fn: any) => {
    /*should be empty*/
  }

  onTouched: any = () => {
    //
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(val: IDropdownObject[] | null): void {
    this.fieldControl.setValue(
      val ? val.map(v => v.name).join(', ') + ', ' : null
    );
    if (val) {
      this.ultimateOutputValue = val;
    }
  }

  /**
   * Subscribe on field control value changing, on every value change called prepareToEmit function with value parameter
   * also called filter function with value parameter and gives result of perform this function to the filteredOptions variable
   * If filter function return empty array filteredOptions will be assigned array with string 'not found'
   */
  ngOnInit() {
    if (this.filterData !== null) {
      this.optionArr = this.filterData!.map(
        (item: IDropdownObject) => item.name
      );
    }
    if (this.countryFilter) {
      this.showHideObj = true;
    }
  }

  ngAfterViewInit(): void {
    this.fieldControl.valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      if (!this.countryFilter) {
        const newOutputValue = this.prepareToEmit(val);
        if (!newOutputValue) {
          if (this.ultimateOutputValue.length) {
            this.ultimateOutputValue = [];
            this.onChange(null);
          }
        } else if (this.ultimateOutputValue.length !== newOutputValue.length) {
          this.ultimateOutputValue = newOutputValue;
          this.onChange(newOutputValue);
        }
      }
    });
    this.filteredOptions = this.fieldControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (this.filter(value).length === 0) {
          this.notFound = true;
          return ['not found'];
        } else {
          this.notFound = false;
          return this.filter(value);
        }
      })
    );
  }

  /**
   * Clean the string value from spaces and check every item of optionArr array is it includes this part of string.
   * If not includes delete this item from array. If input string include coma - separate last part of this string,
   * clean from spaces and start comparing with optionArr item as described above
   *
   * @param { string } value - input value
   * @return string[] optionArr - array with filtered options
   */

  filter(value: string): string[] {
    const cleanValue = value.replace(' ', '');
    if (cleanValue.includes(',')) {
      const tempArr = cleanValue.split(',');
      const lastValue = tempArr.pop();
      const cleanLastValue = lastValue!.trim();
      return this.optionArr!.filter((option: string) =>
        option.toLowerCase().includes(cleanLastValue.toLowerCase())
      );
    } else {
      return this.optionArr!.filter((option: string) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  /**
   * This function called when user clicked on the autocomplete option which popup under input and transfer this item as
   * option parameter inside this function.At first, we get value from the input, cleaned it from spaces. If this string
   * includes coma - all text after coma will be deleted and replaced by selected option, if not - this option with coma
   * and spase after simple pasted to the input field. Also, showPopupOption variable assigned false value (hide popup)
   *
   * @param {string} option - item from the optionArr which was clicked
   * @return void
   */

  chosenOption(option: string): void {
    if (this.countryFilter) {
      this.fieldControl.setValue(option);
      this.showPopupOption = false;
      this.onChange(option);
      return;
    }
    if (this.fieldControl.value?.includes(',')) {
      const tempArr = this.fieldControl.value
        .split(',')
        .map((v: string) => v.trim());
      if (tempArr.includes(option)) {
        this.showPopupOption = false;
        return;
      }
      tempArr.pop();
      const cleanTempStr = tempArr.join(', ');
      this.fieldControl.setValue(`${cleanTempStr}, ${option}, `);
    } else {
      this.fieldControl.setValue(`${option}, `);
    }
    this.showPopupOption = false;
  }

  /**
   * Prepare input string to emit by onChange method to father component.
   * Transform string in to array, trim item spaces and delete duplicate item.
   * Also filter the compareArr array using compare function
   *
   * @param { string } str - input string
   * @return { string[] } compareArr - clean array ready to use in father component
   */
  prepareToEmit(str: string | null): IDropdownObject[] | null {
    if (!str) {
      return null;
    }
    str = str.trim().toLowerCase();
    let emitArr = str.split(',');
    emitArr = emitArr.map(item => item.trim());
    const compareArr = [...new Set(emitArr)];
    const finishArr = compareArr.filter(item => this.compare(item));
    const outputArr = this.filterData!.filter((item: IDropdownObject) =>
      finishArr.includes(item.name.toLowerCase())
    );
    return outputArr.length ? outputArr : null;
  }

  /**
   * Compare item of the compareArr array with every item of optionArr array if optionArr includes the same item function
   * will return true if not return false
   *
   * @param { string } item - item of the compareArr array
   * @return { boolean } status
   */

  compare(item: string): boolean {
    item = item.toLowerCase();
    let status = false;
    for (let option of this.optionArr!) {
      option = option.toLowerCase();
      if (item === option) {
        status = true;
        break;
      }
    }
    return status;
  }

  clearInputValue() {
    if (this.countryFilter) {
      this.fieldControl.setValue(null);
      this.onChange(this.fieldControl.value);
    }
  }

  clearControl() {
    this.fieldControl.setValue(null);
  }
}
